import { ACTIONS, QUESTION_LIST_VARIANTS, STATE_PRESETS } from "./constants"
import { StateType } from "./types"

type ActionType = {
  type: keyof typeof ACTIONS,
}

// load existing state (can be from the localforage cache or can also be a preset!)
type LoadStateActionType = ActionType & {
  state: StateType
}
export const loadState = (state: StateType): LoadStateActionType => ({
  type: ACTIONS.LOAD_STATE,
  state,
})

// reset state to one of the presets
type ResetStateActionType = ActionType & {
  statePreset: keyof typeof STATE_PRESETS,
}
export const resetState = (statePreset: keyof typeof STATE_PRESETS): ResetStateActionType => ({
  type: ACTIONS.RESET_STATE,
  statePreset,
})

// add new question
type AddNewQuestionActionType = ActionType
export const addNewQuestion = (): AddNewQuestionActionType => ({
  type: ACTIONS.ADD_NEW_QUESTION,
})

// add question to interview
type AddQuestionToInterviewActionType = ActionType & {
  questionId: string
  index: number
}
export const addQuestionToInterview = (questionId: string, index: number): AddQuestionToInterviewActionType => ({
  type: ACTIONS.ADD_QUESTION_TO_INTERVIEW,
  questionId,
  index,
})

// move question position in interview
type MoveQuestionInInterviewActionType = ActionType & {
  questionId: string
  index: number
}
export const moveQuestionInInterview = (questionId: string, index: number): MoveQuestionInInterviewActionType => ({
  type: ACTIONS.MOVE_QUESTION_IN_INTERVIEW,
  questionId,
  index,
})

// remove question from interview
type RemoveQuestionFromInterviewActionType = ActionType & {
  questionId: string
}
export const removeQuestionFromInterview = (questionId: string): RemoveQuestionFromInterviewActionType => ({
  type: ACTIONS.REMOVE_QUESTION_FROM_INTERVIEW,
  questionId,
})

// permanently delete question
type PermanentlyDeleteQuestionType = ActionType & {
  questionId: string
}
export const permanentlyDeleteQuestion = (questionId: string): PermanentlyDeleteQuestionType => ({
  type: ACTIONS.PERMANENTLY_DELETE_QUESTION,
  questionId,
})

// toggle edit mode
type ToggleEditingQuestionType = ActionType & {
  questionId: string
}
export const toggleEditingQuestion = (questionId: string): ToggleEditingQuestionType => ({
  type: ACTIONS.TOGGLE_EDITING_QUESTION,
  questionId,
})

// update textEdited
type UpdateQuestionEditedTextType = ActionType & {
  questionId: string
  text: string
}
export const updateQuestionEditedText = (questionId: string, text: string): UpdateQuestionEditedTextType => ({
  type: ACTIONS.UPDATE_QUESTION_EDITED_TEXT,
  questionId,
  text,
})

// revert question to textDefault
type RevertQuestionEditedTextType = ActionType & {
  questionId: string
}
export const revertQuestionEditedText = (questionId: string): RevertQuestionEditedTextType => ({
  type: ACTIONS.REVERT_QUESTION_EDITED_TEXT,
  questionId,
})

// switch on drag mode
type StartDraggingQuestionType = ActionType & {
  questionId: string,
  questionIndex: number,
  questionOrigin: QUESTION_LIST_VARIANTS,
  cardClientRect: DOMRect,
}
export const startDraggingQuestion = (
  questionId: string,
  questionIndex: number,
  questionOrigin: QUESTION_LIST_VARIANTS,
  cardClientRect: DOMRect,
): StartDraggingQuestionType => ({
  type: ACTIONS.START_DRAGGING_QUESTION,
  cardClientRect,
  questionId,
  questionIndex,
  questionOrigin,
})

// update drag drop zone position
type UpdateDropZoneIndex = ActionType & {
  dropZoneIndex?: number
}
export const updateDropZoneIndex = (dropZoneIndex?: number): UpdateDropZoneIndex => ({
  type: ACTIONS.UPDATE_DROP_ZONE_INDEX,
  dropZoneIndex,
})

// switch off drag mode
type StopDraggingQuestionType = ActionType & {
  questionId: string,
}
export const stopDraggingQuestion = (questionId: string): StopDraggingQuestionType => ({
  type: ACTIONS.STOP_DRAGGING_QUESTION,
  questionId,
})

// update job title field
type UpdateJobTitleFieldType = ActionType & {
  jobTitle: string
}
export const updateJobTitleField = (jobTitle: string): UpdateJobTitleFieldType => ({
  type: ACTIONS.UPDATE_JOB_TITLE_FIELD,
  jobTitle,
})

// export interview to Pdf
type ExportToPdf = ActionType
export const exportToPdf = (): ExportToPdf => ({
  type: ACTIONS.EXPORT_TO_PDF
})

export type AllActionsType = ActionType &
  Omit<Partial<
    AddQuestionToInterviewActionType &
    ExportToPdf &
    LoadStateActionType &
    MoveQuestionInInterviewActionType &
    PermanentlyDeleteQuestionType &
    RemoveQuestionFromInterviewActionType &
    ResetStateActionType &
    RevertQuestionEditedTextType &
    StartDraggingQuestionType &
    StopDraggingQuestionType &
    ToggleEditingQuestionType &
    UpdateDropZoneIndex &
    UpdateJobTitleFieldType &
    UpdateQuestionEditedTextType
  >, "type">
